
import React from 'react';
import { MessageInput } from './MessageInput';
import { cn } from '@/lib/utils';
import { MessageOptions } from '@/types/openai';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';

interface InputFooterProps {
  onSendMessage: (options: MessageOptions) => void;
  onUsageCheck: (topic: 'guide' | 'performance' | 'trading_strategy' | 'advisor') => Promise<boolean>;
  isDisabled?: boolean;
  topic: 'guide' | 'performance' | 'trading_strategy' | 'advisor';
  isOnline: boolean;
  isExpertMode: boolean;
  onModeToggle: () => void;
}

export const InputFooter: React.FC<InputFooterProps> = ({
  onSendMessage,
  onUsageCheck,
  isDisabled = false,
  topic,
  isOnline,
  isExpertMode,
  onModeToggle
}) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  return (
    <div 
      className={cn(
        // Sticky positioning within the chat area
        "sticky bottom-0 z-30",
        // Constraint container to match message bubbles width
        "max-w-4xl mx-auto w-full",
        // Background with stronger blur and shadow
        "bg-background/95 backdrop-blur-xl",
        // Rounded top corners like ChatGPT
        "rounded-t-2xl",
        // Enhanced shadow for better visual separation
        "shadow-xl border-t border-l border-r border-border/20",
        // Optimized padding - much smaller on mobile
        isMobile ? "px-3 pt-2 pb-1" : "px-6 pt-3 pb-2"
      )}
      style={{
        // Safe area handling - stick to keyboard without gap
        paddingBottom: isMobile ? 'calc(0.25rem + var(--safe-area-inset-bottom))' : '0.5rem',
        // Enhanced backdrop blur
        backdropFilter: 'blur(20px)',
      }}
    >
      {/* Subtle gradient overlay at the top for visual integration */}
      <div 
        className="absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-transparent via-background/10 to-background/30 pointer-events-none rounded-t-2xl"
        aria-hidden="true"
      />
      
      {/* Content container */}
      <div className={cn(
        "relative w-full",
        !isOnline && "opacity-50 pointer-events-none"
      )}>
        <MessageInput
          onSendMessage={onSendMessage}
          onUsageCheck={onUsageCheck}
          isDisabled={isDisabled || !isOnline}
          topic={topic}
          isExpertMode={isExpertMode}
          onModeToggle={onModeToggle}
        />
        
        {!isOnline && (
          <p className="text-xs text-muted-foreground/70 text-center mt-2 font-medium">
            {t('ai_assistant.offline_message')}
          </p>
        )}
      </div>
    </div>
  );
};
